import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useContext, useState} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {EventWizardProgressStep, EventWizardSourceType, orderedProgressSteps, PROGRESS_STEP_LABEL_KEYS} from '@Components/event-wizard/event-wizard.types';
import {SteppedProgressBar} from '@Components/stepped-progress-bar/stepped-progress-bar';
import {saveEvent} from '@Components/event-wizard/event-wizard.helper';
import {
  useEventWizardCoverPhoto,
  useEventWizardCurrentProgressStep,
  useEventWizardDescription,
  useEventWizardEndDateTime,
  useEventWizardIsPaidTicketingFeatureAvailable,
  useEventWizardIsTicketingCountrySupported,
  useEventWizardOrganisers,
  useEventWizardPageSourceType,
  useEventWizardLegibilityStatus,
  useEventWizardReduxState,
  useEventWizardRegistrationDetails,
  useEventWizardSelectedRegistrationMode,
  useEventWizardShouldNextBtnBeDisabled,
  useEventWizardStartDateTime,
  useEventWizardTicketingConfig,
  useEventWizardTimeZone,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {isPaidTicketingConfig, isStep2Complete} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {requireTicketingSelection} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';
import {LegibilityStatus} from '@Libraries/event-colors.library';
import {
  onEventWizardCheckErrorStateUpdate,
  onEventWizardIsEventBeingCreatedUpdate, onEventWizardIsStylesPanelOpenUpdate,
  onEventWizardProgressStepUpdate,
  onEventWizardUpdateShouldExpandReviewSection,
} from '@Components/event-wizard/event-wizard-slice';
import {useDispatch} from 'react-redux';
import {
  isBasicSectionComplete,
  isCoverPhotoSectionComplete,
  isMediaSectionComplete,
  isOrganiserSectionComplete,
} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {ProgressDots} from '@Components/progress-dots/progress-dots';
import {toggleDisplayPreviewCoverPhotoSection} from '@Components/event-wizard/preview/preview.helper';
import styles from './event-footer.module.scss';
import {EventWizardTestId} from '@/test-ids/event-wizard-test-id';
import {authenticateUser, isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from "@Modals/error-modal";
import {GA4EventName} from "@Libraries/ga-events";

export function EventFooter(): ReactElement<any> {
  const dispatch = useDispatch();
  const eventWizardReduxState = useEventWizardReduxState();
  const currentStep = useEventWizardCurrentProgressStep();
  const currentStepIndex = orderedProgressSteps.indexOf(currentStep);
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const title = useEventWizardTitle();
  const startDateTime = useEventWizardStartDateTime();
  const endDateTime = useEventWizardEndDateTime();
  const timezone = useEventWizardTimeZone();
  const venue = useEventWizardVenue();
  const coverPhoto = useEventWizardCoverPhoto();
  const desc = useEventWizardDescription();
  const organisers = useEventWizardOrganisers();
  const shouldNextBtnBeDisabled = useEventWizardShouldNextBtnBeDisabled();
  const legibilityStatus = useEventWizardLegibilityStatus();
  const pageSourceType = useEventWizardPageSourceType();
  const ticketingConfig = useEventWizardTicketingConfig();
  const registrationDetails = useEventWizardRegistrationDetails();
  const selectedRegistrationMode = useEventWizardSelectedRegistrationMode();
  const isPaidTicketingFeatureAvailable = useEventWizardIsPaidTicketingFeatureAvailable();
  const isTicketingCountrySupported = useEventWizardIsTicketingCountrySupported();

  const [isNextBtnLoading, setIsNextBtnLoading] = useState<boolean>(false);

  useEffect(() => {
    if (areRequirementsToSaveComplete()) {
      setIsNextBtnLoading(true);
      void authenticateUser(onAuthenticatedUserSaveEvent, resetLoadingStates, {
        panelText: window.i18next.t('pmwjs_make_account_to_share_event'),
        panelSubtext: window.i18next.t('pmwjs_login_event_promotion_subtext')
      });
    }
  }, [eventWizardReduxState.isEventBeingCreated, eventWizardReduxState.types])

  const onAuthenticatedUserSaveEvent = (): void => {
    void saveEvent(eventWizardReduxState, onSaveEventFail, pageSourceType);
  };

  /**
   * The legibility gate lives here rather than on the styles panel's Done button.
   *
   * Done only moves the panel back to its default section — it commits nothing, and the palette is
   * already in Redux the moment a colour is picked — so disabling it never protected the save; it
   * only trapped the user in the colour section while back and close stayed open. Blocking the step
   * is the guard that actually holds, and it is the same button that saves in UPDATE mode.
   *
   * `POOR` only. `WARNING` is a Mode A report about a CTA fill that disappears into the page, which
   * the save endpoint does not reject and which every pale-brand event saved before this feature is
   * already in — blocking on it would strand them on their next edit.
   */
  const isNextBlocked = (): boolean => {
    return shouldNextBtnBeDisabled || legibilityStatus === LegibilityStatus.POOR;
  };

  const areRequirementsToSaveComplete = (): boolean => {
    return eventWizardReduxState.isEventBeingCreated &&
      !eventWizardReduxState.types.isAITypeLoading &&
      eventWizardReduxState.types.selectedType?.hashedID !== null;
  }

  const getStepLabels = (): string[] => {
    return orderedProgressSteps.map((step) => window.i18next.t(PROGRESS_STEP_LABEL_KEYS[step]));
  };

  const onSaveEventFail = (showErrModal = false): void => {
    setIsNextBtnLoading(false);
    dispatch(onEventWizardIsEventBeingCreatedUpdate(false));

    if (showErrModal) {
      openErrorModal({
        message: window.i18next.t('pmwjs_error_message'),
        log: false
      });
    }
  }

  const resetLoadingStates = (): void => {
    setIsNextBtnLoading(false);
    dispatch(onEventWizardIsEventBeingCreatedUpdate(false));
  };

  const getNextStepLabel = (): string => {
    if (isLastStep()) {
      if (pageSourceType === EventWizardSourceType.UPDATE) {
        return window.i18next.t('pmwjs_update');
      }
      return window.i18next.t('pmwjs_create');
    }
    return window.i18next.t('pmwjs_next');
  };

  const goBackStep = (): void => {
    if (isFirstStep()) {
      return;
    }
    updateProgressStep(orderedProgressSteps[currentStepIndex - 1]);

    if (!isCoverPhotoSectionComplete(coverPhoto)) {
      toggleDisplayPreviewCoverPhotoSection(false);
    }
  };

  const advanceToNextStep = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENT_WIZARD_COMPLETE_FIRST_STEP);
    updateProgressStep(orderedProgressSteps[currentStepIndex + 1]);
    toggleDisplayPreviewCoverPhotoSection();
    setIsNextBtnLoading(false);
  };

  const isPaidTicketingStep = (): boolean => {
    return (
      orderedProgressSteps[currentStepIndex] === EventWizardProgressStep.TICKETING &&
      isPaidTicketingConfig(selectedRegistrationMode, ticketingConfig)
    );
  };

  /**
   * Step 2 -> 3 on paid ticketing, once we know who the organiser is.
   *
   * Synchronous, and deliberately: the Stripe onboarding gate that used to live here is gone, because a paid
   * ticketed event can now be created with no Stripe account and the Tickets page picks the organiser up on
   * arrival. What is left is the eligibility verdict, shared with the Tickets tile so the two cannot drift.
   * The helper has already opened the right dialog when it says no.
   */
  const onTicketingStepAuthenticated = (): void => {
    if (!requireTicketingSelection(isPaidTicketingFeatureAvailable, isTicketingCountrySupported)) {
      setIsNextBtnLoading(false);
      return;
    }
    advanceToNextStep();
  };

  const scrollFirstErrorIntoView = (): void => {
    requestAnimationFrame((): void => {
      const firstError = document.querySelector('.js-event-wizard-error');
      if (firstError === null) {
        return;
      }
      const rect = firstError.getBoundingClientRect();
      const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!isInView) {
        firstError.scrollIntoView({behavior: 'smooth', block: 'center'});
      }
    });
  };

  const goToNextStep = (): void => {
    dispatch(onEventWizardIsStylesPanelOpenUpdate(false));
    setIsNextBtnLoading(true);

    if (!areRequiredSectionsComplete()) {
      dispatch(onEventWizardCheckErrorStateUpdate(true));
      dispatch(onEventWizardUpdateShouldExpandReviewSection(true));
      setIsNextBtnLoading(false);
      scrollFirstErrorIntoView();
      return;
    }
    dispatch(onEventWizardCheckErrorStateUpdate(false));

    if (isLastStep()) {
      dispatch(onEventWizardIsEventBeingCreatedUpdate(true));
      return;
    }

    if (isPaidTicketingStep()) {
      if (!isUserLoggedIn()) {
        void authenticateUser(onTicketingStepAuthenticated, resetLoadingStates, {
          panelText: window.i18next.t('pmwjs_event_ticketing_login_to_sell'),
        });
        return;
      }
      onTicketingStepAuthenticated();
      return;
    }

    advanceToNextStep();
  };

  const updateProgressStep = (step: EventWizardProgressStep): void => {
    dispatch(onEventWizardProgressStepUpdate(step));
  };

  const isFirstStep = (): boolean => {
    return currentStepIndex === 0;
  };

  const isLastStep = (): boolean => {
    return currentStepIndex === orderedProgressSteps.length - 1;
  };

  const areRequiredSectionsComplete = (): boolean => {
    if (orderedProgressSteps[currentStepIndex] === EventWizardProgressStep.OVERVIEW) {
      return isBasicSectionComplete(title, venue, startDateTime, endDateTime, timezone);
    }
    if (orderedProgressSteps[currentStepIndex] === EventWizardProgressStep.TICKETING) {
      return isStep2Complete(selectedRegistrationMode, ticketingConfig, registrationDetails);
    }
    return (
      isBasicSectionComplete(title, venue, startDateTime, endDateTime, timezone) &&
      isStep2Complete(selectedRegistrationMode, ticketingConfig, registrationDetails) &&
      isMediaSectionComplete(desc, coverPhoto) &&
      isOrganiserSectionComplete(organisers.selectedOrganiser)
    );
  };

  const getProgressSteps = (): ReactElement<any> => {
    return isSmallScreen ? (
        <ProgressDots currentStep={currentStepIndex + 1} numDots={orderedProgressSteps.length} />
      ) : (
        <SteppedProgressBar className="spacing-m-b-3" currentStep={currentStepIndex + 1} numSteps={orderedProgressSteps.length} labels={getStepLabels()} />
      )
  }

  const handleCancel = (): void => {
    window.history.back();
  };

  const showCancelButton = pageSourceType === EventWizardSourceType.UPDATE && isFirstStep();

  return (
    <div
      className={`${styles.footerContainer} ${isSmallScreen ? 'spacing-p-l-3 spacing-p-r-3' : 'spacing-p-l-12 spacing-p-r-12'} bg-neutral-0 flex-row-justify-center _full-width spacing-p-t-3 spacing-p-b-3 shadow-static-elevation-2 border-s-standard _no-border-right _no-border-left _no-border-bottom`}
    >
      <div className={`${styles.contentContainer} _full-width flex-row-align-center justify-content-between `}>
        <Button
          onClick={showCancelButton ? handleCancel : goBackStep}
          customClasses={showCancelButton ? '' : (isFirstStep() ? '_invisible' : '')}
          type={Type.SECONDARY}
          size={Size.SMALL}
          icon={showCancelButton ? undefined : "icon-arrow-left"}
          text={showCancelButton ? window.i18next.t('pmwjs_cancel') : window.i18next.t('pmwjs_back')}
        />

        {getProgressSteps()}

        <Button
          isLoading={isNextBtnLoading}
          disabled={isNextBlocked()}
          onClick={goToNextStep}
          data-testid={EventWizardTestId.FOOTER_PRIMARY_BUTTON}
          type={Type.PRIMARY}
          size={Size.SMALL}
          icon="icon-arrow-right"
          iconPlacement={IconPlacement.RIGHT}
          text={getNextStepLabel()}
        />
      </div>
    </div>
  );
}
